
import React, { useState } from "react";
import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Image, FileImage } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import MediaLibrary from "../MediaLibrary";
import { AlertDialog, AlertDialogContent } from "@/components/ui/alert-dialog";
import { GalleryImage } from "./extensions/GalleryExtension";

interface GalleryFormProps {
  editor: Editor;
  onClose: () => void;
  uploadImage?: (file: File) => Promise<string>;
}

type GalleryLayout = "grid" | "slider" | "masonry";

const GalleryForm = ({ editor, onClose, uploadImage }: GalleryFormProps) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [galleryLayout, setGalleryLayout] = useState<GalleryLayout>("grid");
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);

  const handleAddImage = () => {
    setImages([...images, { url: "" }]);
    setCurrentEditIndex(images.length);
  };

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
    if (currentEditIndex === index) {
      setCurrentEditIndex(null);
    }
  };

  const handleImageChange = (index: number, field: keyof GalleryImage, value: string) => {
    setImages(
      images.map((img, i) => (i === index ? { ...img, [field]: value } : img))
    );
  };

  const handleImageSelect = (url: string) => {
    if (currentEditIndex !== null) {
      handleImageChange(currentEditIndex, 'url', url);
      setIsMediaLibraryOpen(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!uploadImage) return "";
    try {
      const url = await uploadImage(file);
      return url;
    } catch (error) {
      console.error("Error uploading image:", error);
      return "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out images without URLs
    const validImages = images.filter(img => img.url);
    if (validImages.length === 0) return;

    // Use our new gallery extension command
    editor
      .chain()
      .focus()
      .setGallery({
        images: validImages,
        layout: galleryLayout,
        title: galleryTitle || undefined,
      })
      .run();

    onClose();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-1">
        <div className="space-y-2">
          <Label htmlFor="gallery-title">Gallery Title (optional)</Label>
          <Input
            id="gallery-title"
            placeholder="Enter a title for the gallery"
            value={galleryTitle}
            onChange={(e) => setGalleryTitle(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="gallery-layout">Gallery Layout</Label>
          <Select
            value={galleryLayout}
            onValueChange={(value) => setGalleryLayout(value as GalleryLayout)}
          >
            <SelectTrigger id="gallery-layout">
              <SelectValue placeholder="Select layout style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="grid">Grid</SelectItem>
              <SelectItem value="slider">Slider</SelectItem>
              <SelectItem value="masonry">Masonry</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Gallery Images</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddImage}
              className="h-8"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Image
            </Button>
          </div>

          <ScrollArea className="h-[300px] border rounded-md p-2">
            {images.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4 text-muted-foreground">
                <FileImage className="h-10 w-10 mb-2" />
                <p>No images added to gallery.</p>
                <p className="text-sm">Click 'Add Image' to begin.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {images.map((image, index) => (
                  <div key={index} className="p-3 border rounded-md relative">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6"
                      onClick={() => handleRemoveImage(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Image URL"
                          value={image.url}
                          onChange={(e) => handleImageChange(index, 'url', e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setCurrentEditIndex(index);
                            setIsMediaLibraryOpen(true);
                          }}
                        >
                          <Image className="h-4 w-4 mr-1" /> Browse
                        </Button>
                      </div>

                      <Input
                        placeholder="Alt text (for accessibility)"
                        value={image.altText || ""}
                        onChange={(e) => handleImageChange(index, 'altText', e.target.value)}
                      />

                      <Textarea
                        placeholder="Caption (optional)"
                        value={image.caption || ""}
                        onChange={(e) => handleImageChange(index, 'caption', e.target.value)}
                        rows={2}
                      />

                      {image.url && (
                        <div className="mt-2 max-h-24 overflow-hidden">
                          <img
                            src={image.url}
                            alt={image.altText || "Gallery image"}
                            className="w-full object-cover rounded"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <Button
          type="submit"
          disabled={images.filter(img => img.url).length === 0}
          className="w-full"
        >
          Insert Gallery
        </Button>
      </form>

      <AlertDialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
        <AlertDialogContent className="max-w-4xl">
          <MediaLibrary
            onSelect={handleImageSelect}
            onClose={() => setIsMediaLibraryOpen(false)}
            onUpload={handleFileUpload}
          />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GalleryForm;
